import type { BaseComponentProps } from "@neru/types";
import { deduplicateClass, isEmail } from "@neru/utils";
import {
  type ChangeEvent,
  type FocusEvent,
  type KeyboardEvent,
  useEffect,
  useState,
} from "react";

import { FORM_LABEL_STYLES } from "../../constants";
import { Button } from "../Button/Button";
import { Icon, type IconType } from "../Icon/Icon";

export interface InputProps extends BaseComponentProps {
  disabled?: boolean;
  icon?: IconType;
  label?: string;
  name: string;
  onClear?: () => Promise<void> | void;
  onInput?: (event: ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
  placeholder?: string;
  required?: boolean;
  showValidationMessage?: boolean;
  type?: "text" | "email" | "password" | "number" | "search" | "tel" | "url";
  value?: string;
  withClearButton?: boolean;
}

const getInputStyle = (hasError: boolean, disabled: boolean) => {
  if (hasError) {
    return "border-alert-500 bg-alert-50 dark:border-alert-400 dark:bg-alert-900";
  } else if (disabled) {
    return "border-primary-300";
  } else {
    return "border-primary-950 dark:border-primary-300 placeholder-gray-300";
  }
};

export const Input = ({
  className,
  "data-testid": testId = "input",
  disabled = false,
  icon,
  label,
  name,
  onClear,
  onInput,
  onKeyDown,
  placeholder,
  required = false,
  showValidationMessage = false,
  type = "text",
  value = "",
  withClearButton = false,
}: InputProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const [touched, setTouched] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [inputValue, setInputValue] = useState(value);

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    setTouched(true);
    const value = event.target.value;

    if (required && !value) {
      setHasError(true);
    } else if (type === "email" && value && !isEmail(value)) {
      setHasError(true);
    } else {
      setHasError(false);
    }
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onInput && onInput(event);
    const value = event.target.value;
    setInputValue(value);
    setHasError(false);

    const isTextInputError = required && value === "";
    const isEmailInputError = type === "email" && value && !isEmail(value);

    if (touched && (isTextInputError || isEmailInputError)) {
      setHasError(true);
    }
  };

  const handleClear = () => {
    setHasError(false);
    setTouched(false);
    onClear ? onClear() : setInputValue("");
  };

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const focusStyle = isFocused
    ? "focus:ring-1 focus:ring-primary-950 focus:border-primary-950 dark:focus:ring-primary-50 dark:focus:border-primary-50"
    : "";
  const disabledStyle = disabled
    ? "bg-primary-100 dark:bg-primary-900 text-primary-300 dark:text-primary-600 cursor-not-allowed"
    : "";

  const inputClasses = `w-full min-w-20 ${icon ? "pl-10" : "pl-4"} ${withClearButton ? "pr-10" : "pr-4"} bg-primary-0 dark:bg-primary-950 dark:text-primary-0 h-10 border rounded-lg focus:outline-none
  ${getInputStyle(hasError, disabled)}
  ${focusStyle}
  ${disabledStyle}
`;

  const iconStyles = "absolute top-1/2 left-3 -translate-y-1/2";
  const buttonStyles = "absolute top-1/2 right-1 -translate-y-1/2";

  const inputIcon = icon && (
    <Icon
      className={iconStyles}
      connotation="primary"
      customSize={20}
      data-testid={`icon-${icon}`}
      type={icon}
    />
  );

  return (
    <div className="mx-px">
      {label ? (
        <label className={FORM_LABEL_STYLES} data-testid={`label-${testId}`}>
          {label}
        </label>
      ) : null}
      <div className="relative">
        {inputIcon}
        <input
          className={deduplicateClass(inputClasses, className)}
          data-testid={testId}
          disabled={disabled}
          name={name}
          onBlur={handleBlur}
          onChange={handleChange}
          onFocus={handleFocus}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          type={type}
          value={inputValue}
        />
        {withClearButton && (
          <Button
            aria-label="Clear input"
            className={buttonStyles}
            data-testid="clear-search-input"
            icon="close-line"
            onClick={handleClear}
            size="condensed"
          />
        )}
      </div>

      {showValidationMessage && (
        <p
          className={`
            dark:text-primary-0
            mt-1 flex h-5 items-center
          `}
        >
          {hasError && (
            <>
              <Icon
                className="fill-alert-500 mr-1"
                size="small"
                type="info-line"
              />
              {label} is required
            </>
          )}
        </p>
      )}
    </div>
  );
};
